import {
    Box3,
    BoxGeometry,
    BufferGeometry,
    CylinderGeometry,
    DoubleSide,
    Group,
    Mesh,
    MeshStandardMaterial,
    PlaneGeometry,
    Quaternion,
    SphereGeometry,
    Vector3,
} from "three";
import type { Camera } from "three";
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js";
import { samplePalette } from "./gridCubesGeometry";
import type { GridCubesDrawable } from "./gridCubesGeometry";
import { hashPlacementToUnit } from "./pathPopulatePlacement";
import { startFrameBudget, yieldIfFrameExhausted } from "./cooperativeScheduler";

// --- Data model -----------------------------------------------------------

export type VolumeShape = "cuboid" | "cylinder" | "sphere" | "billboard";

export interface Vec3 {
    x: number;
    y: number;
    z: number;
}

/** "free" keeps each path's authored placement; "bin-pack" collision-packs copies. */
export type PackingMode = "free" | "bin-pack";

export interface PackingConfig {
    mode: PackingMode;
    /** Copies of this path's volume to instantiate (>= 1). */
    count: number;
    /** Minimum gap between packed boxes (world units). */
    margin: number;
    /** Deterministic placement seed. */
    seed: number;
}

/** Object types that can be packed into a container volume. */
export type FillObjectType = "cuboid" | "cylinder" | "sphere" | "billboard";

/**
 * Per-path "fill" config. When enabled, the path's shape becomes an invisible
 * container volume that is subdivided into a 3D lattice; slots whose center
 * lies inside the volume are filled with small packed objects.
 */
export interface FillConfig {
    enabled: boolean;
    /** Lattice resolution per axis (slots span the container's AABB). */
    resolution: number;
    /** Which object types to place (cycled deterministically per slot). */
    objectTypes: FillObjectType[];
    /** Object size as a fraction of the slot size (0..1). */
    objectScale: number;
    /** Deterministic type/jitter seed. */
    seed: number;
}

export interface Path3D {
    id: string;
    name: string;
    /** Ordered polyline (spine); needs >= 2 points for an extruded volume. */
    points: Vec3[];
    shape: VolumeShape;
    /** Cuboid half-width / cylinder|sphere radius / billboard half-extent. */
    crossSize: number;
    /** Radial tessellation for cylinder/sphere (capped). */
    radialSegments: number;
    color: string;
    packing: PackingConfig;
    /** Container-fill packing (overrides the bare primitive when enabled). */
    fill: FillConfig;
    visible: boolean;
}

export interface Box3Lite {
    min: Vec3;
    max: Vec3;
}

/** Polymorphic scene object; groups nest. */
export type SceneObject =
    | {
          kind: "primitive";
          shape: Exclude<VolumeShape, "billboard">;
          spine: Vec3[];
          crossSize: number;
          radialSegments: number;
          color: string;
          sourcePathId: string;
      }
    | {
          kind: "billboard";
          center: Vec3;
          halfExtent: number;
          color: string;
          sourcePathId: string;
      }
    | {
          kind: "group";
          children: SceneObject[];
          /** Local-space AABB of the children (used by the packer). */
          bounds: Box3Lite;
          /** World offset applied to children (set by the packer). */
          offset: Vec3;
          sourcePathId: string;
      };

export interface PackingBounds {
    x: number;
    y: number;
    z: number;
}

export interface VolumeProjectionParams {
    palette: string[];
    /** 0..1 ambient floor so faces away from light are not fully dark. */
    ambient: number;
    backfaceCulling: boolean;
    faceStrokeEnabled: boolean;
    faceStrokeColor: string;
    faceStrokeWidth: number;
    outputMinX: number;
    outputMinY: number;
    outputWidth: number;
    outputHeight: number;
    signal: AbortSignal;
    onProgress?: (info: VolumeProgress) => void;
}

export type VolumeProgressPhase = "build" | "project";

export interface VolumeProgress {
    phase: VolumeProgressPhase;
    processed: number;
    total: number;
}

export type VolumeDrawable = Extract<GridCubesDrawable, { kind: "face" }>;

export interface VolumeComputeResult {
    drawables: VolumeDrawable[];
    objectCount: number;
    faceCount: number;
}

export const RADIAL_SEGMENTS_MIN = 3;
export const RADIAL_SEGMENTS_MAX = 48;
const MAX_CONFIGURED_COPIES = 200;
/** Direction a primitive's local geometry is built along before orientation. */
const LOCAL_UP = new Vector3(0, 1, 0);
/** Light direction for face shading (matches Grid Cubes 3D). */
const LIGHT_DIR = new Vector3(0.32, 0.86, 0.4).normalize();

// --- Small helpers --------------------------------------------------------

function clamp01(v: number): number {
    return Math.max(0, Math.min(1, v));
}

function toVector3(p: Vec3): Vector3 {
    return new Vector3(p.x, p.y, p.z);
}

function polylineMidpoint(points: Vec3[]): Vec3 {
    if (points.length === 0) return { x: 0, y: 0, z: 0 };
    const acc = points.reduce(
        (a, p) => {
            a.x += p.x;
            a.y += p.y;
            a.z += p.z;
            return a;
        },
        { x: 0, y: 0, z: 0 },
    );
    const n = points.length;
    return { x: acc.x / n, y: acc.y / n, z: acc.z / n };
}

export function createDefaultPath(index: number): Path3D {
    return {
        id:
            typeof crypto !== "undefined" && "randomUUID" in crypto
                ? crypto.randomUUID()
                : `path-${Date.now()}-${index}`,
        name: `Path ${index + 1}`,
        points: [
            { x: 0, y: 1.2, z: 0 },
            { x: 0, y: -1.2, z: 0 },
        ],
        shape: "cuboid",
        crossSize: 0.5,
        radialSegments: 16,
        color: "#7ca7ff",
        packing: { mode: "free", count: 1, margin: 0.1, seed: 1 },
        fill: {
            enabled: false,
            resolution: 5,
            objectTypes: ["cuboid"],
            objectScale: 0.8,
            seed: 1,
        },
        visible: true,
    };
}

export const FILL_RESOLUTION_MIN = 1;
export const FILL_RESOLUTION_MAX = 24;

/**
 * Backfill defaults onto a (possibly older/persisted) path so newer fields
 * like `fill` and `packing` are always present.
 */
export function normalizePath(raw: Partial<Path3D> & { id?: string }): Path3D {
    const def = createDefaultPath(0);
    return {
        id: raw.id ?? def.id,
        name: raw.name ?? def.name,
        points:
            Array.isArray(raw.points) && raw.points.length >= 1
                ? raw.points.map((p) => ({
                      x: Number(p.x) || 0,
                      y: Number(p.y) || 0,
                      z: Number(p.z) || 0,
                  }))
                : def.points,
        shape: raw.shape ?? def.shape,
        crossSize: raw.crossSize ?? def.crossSize,
        radialSegments: raw.radialSegments ?? def.radialSegments,
        color: raw.color ?? def.color,
        packing: { ...def.packing, ...(raw.packing ?? {}) },
        fill: {
            ...def.fill,
            ...(raw.fill ?? {}),
            objectTypes:
                Array.isArray(raw.fill?.objectTypes) &&
                raw.fill.objectTypes.length > 0
                    ? raw.fill.objectTypes
                    : def.fill.objectTypes,
        },
        visible: raw.visible ?? def.visible,
    };
}

// --- Path -> SceneObject compilation --------------------------------------

/** Build a single base object (no packing copies) from a path. */
function baseObjectForPath(path: Path3D): SceneObject {
    if (path.shape === "billboard") {
        return {
            kind: "billboard",
            center: polylineMidpoint(path.points),
            halfExtent: Math.max(0.01, path.crossSize),
            color: path.color,
            sourcePathId: path.id,
        };
    }
    return {
        kind: "primitive",
        shape: path.shape,
        spine: path.points.map((p) => ({ ...p })),
        crossSize: Math.max(0.01, path.crossSize),
        radialSegments: Math.max(
            RADIAL_SEGMENTS_MIN,
            Math.min(RADIAL_SEGMENTS_MAX, Math.round(path.radialSegments)),
        ),
        color: path.color,
        sourcePathId: path.id,
    };
}

// --- Container fill -------------------------------------------------------

/** Squared distance from point p to segment ab. */
function distSqToSegment(p: Vector3, a: Vector3, b: Vector3): number {
    const ab = b.clone().sub(a);
    const lenSq = ab.lengthSq();
    if (lenSq < 1e-12) return p.distanceToSquared(a);
    const t = Math.max(0, Math.min(1, p.clone().sub(a).dot(ab) / lenSq));
    const proj = a.clone().addScaledVector(ab, t);
    return p.distanceToSquared(proj);
}

/**
 * Is world point `p` inside the container volume defined by a spine + crossSize?
 * cuboid/cylinder = within crossSize of the spine polyline (capsule region);
 * sphere = within crossSize of any spine vertex (bead chain).
 */
function isInsideContainer(
    p: Vector3,
    spine: Vector3[],
    shape: Exclude<VolumeShape, "billboard">,
    crossSize: number,
): boolean {
    const r2 = crossSize * crossSize;
    if (shape === "sphere") {
        for (const v of spine) {
            if (p.distanceToSquared(v) <= r2) return true;
        }
        return false;
    }
    for (let i = 0; i < spine.length - 1; i++) {
        if (distSqToSegment(p, spine[i]!, spine[i + 1]!) <= r2) return true;
    }
    return false;
}

/**
 * Fill a path's container volume with packed objects: subdivide the container
 * AABB into a `resolution^3` lattice and place one object at each in-volume
 * slot center. Object type cycles deterministically through `objectTypes`.
 */
export function fillContainer(path: Path3D): SceneObject[] {
    const { fill, shape, crossSize, color } = path;
    if (shape === "billboard") return [];
    const spine = path.points.map(toVector3);
    if (spine.length < 1) return [];

    // Container AABB (spine extent padded by crossSize).
    const box = new Box3();
    for (const v of spine) {
        box.expandByPoint(v.clone().addScalar(crossSize));
        box.expandByPoint(v.clone().addScalar(-crossSize));
    }
    if (box.isEmpty()) return [];
    const size = box.getSize(new Vector3());
    const res = Math.max(
        FILL_RESOLUTION_MIN,
        Math.min(FILL_RESOLUTION_MAX, Math.round(fill.resolution)),
    );
    // Slot dimensions per axis (cells span the AABB).
    const slot = new Vector3(
        size.x / res,
        size.y / res,
        size.z / res,
    );
    const slotMin = Math.max(1e-4, Math.min(slot.x, slot.y, slot.z));
    const objHalf = (slotMin * Math.max(0.05, Math.min(1, fill.objectScale))) / 2;
    const types =
        fill.objectTypes.length > 0 ? fill.objectTypes : (["cuboid"] as const);

    const out: SceneObject[] = [];
    let slotIndex = 0;
    for (let ix = 0; ix < res; ix++) {
        for (let iy = 0; iy < res; iy++) {
            for (let iz = 0; iz < res; iz++) {
                const center = new Vector3(
                    box.min.x + (ix + 0.5) * slot.x,
                    box.min.y + (iy + 0.5) * slot.y,
                    box.min.z + (iz + 0.5) * slot.z,
                );
                if (!isInsideContainer(center, spine, shape, crossSize)) {
                    continue;
                }
                const pick =
                    types[
                        Math.floor(
                            hashPlacementToUnit(fill.seed, slotIndex, 0x55aa) *
                                types.length,
                        ) % types.length
                    ]!;
                slotIndex++;
                const c: Vec3 = { x: center.x, y: center.y, z: center.z };
                if (pick === "billboard") {
                    out.push({
                        kind: "billboard",
                        center: c,
                        halfExtent: objHalf,
                        color,
                        sourcePathId: path.id,
                    });
                } else {
                    out.push({
                        kind: "primitive",
                        shape: pick,
                        spine: [
                            { x: c.x, y: c.y + objHalf, z: c.z },
                            { x: c.x, y: c.y - objHalf, z: c.z },
                        ],
                        crossSize: objHalf,
                        radialSegments: Math.max(
                            RADIAL_SEGMENTS_MIN,
                            Math.min(RADIAL_SEGMENTS_MAX, path.radialSegments),
                        ),
                        color,
                        sourcePathId: path.id,
                    });
                }
            }
        }
    }
    return out;
}

/** Axis-aligned bounds of an object in its own local space (offset = 0). */
function objectLocalBounds(obj: SceneObject): Box3Lite {
    const box = new Box3();
    const accumulate = (o: SceneObject, parentOffset: Vector3) => {
        if (o.kind === "group") {
            const off = parentOffset.clone().add(toVector3(o.offset));
            for (const child of o.children) accumulate(child, off);
            return;
        }
        if (o.kind === "billboard") {
            const c = toVector3(o.center).add(parentOffset);
            box.expandByPoint(c.clone().addScalar(o.halfExtent));
            box.expandByPoint(c.clone().addScalar(-o.halfExtent));
            return;
        }
        // primitive: expand by each spine point +/- crossSize
        for (const p of o.spine) {
            const c = toVector3(p).add(parentOffset);
            box.expandByPoint(c.clone().addScalar(o.crossSize));
            box.expandByPoint(c.clone().addScalar(-o.crossSize));
        }
    };
    accumulate(obj, new Vector3());
    if (box.isEmpty()) {
        return { min: { x: 0, y: 0, z: 0 }, max: { x: 0, y: 0, z: 0 } };
    }
    return {
        min: { x: box.min.x, y: box.min.y, z: box.min.z },
        max: { x: box.max.x, y: box.max.y, z: box.max.z },
    };
}

/**
 * Compile paths into scene objects. Each path yields `packing.count` copies;
 * when count > 1 they are wrapped in a group whose children carry deterministic
 * offsets (later overwritten by the packer for bin-pack mode).
 */
export function compilePaths(paths: Path3D[]): SceneObject[] {
    const objects: SceneObject[] = [];
    for (const path of paths) {
        if (!path.visible) continue;
        if (path.shape !== "billboard" && path.points.length < 2) continue;
        const count = Math.max(
            1,
            Math.min(MAX_CONFIGURED_COPIES, Math.round(path.packing.count)),
        );

        // When fill is enabled, the path is an invisible container packed with
        // small objects. The whole filled container is one "unit" for copies.
        const makeUnit = (): SceneObject => {
            if (path.fill.enabled && path.shape !== "billboard") {
                const children = fillContainer(path);
                const group: SceneObject = {
                    kind: "group",
                    children,
                    bounds: { min: { x: 0, y: 0, z: 0 }, max: { x: 0, y: 0, z: 0 } },
                    offset: { x: 0, y: 0, z: 0 },
                    sourcePathId: path.id,
                };
                group.bounds = objectLocalBounds(group);
                return group;
            }
            return baseObjectForPath(path);
        };

        if (count === 1) {
            objects.push(makeUnit());
            continue;
        }
        const children: SceneObject[] = [];
        for (let i = 0; i < count; i++) {
            children.push(makeUnit());
        }
        const base = children[0]!;
        objects.push({
            kind: "group",
            children,
            bounds: objectLocalBounds(base),
            offset: { x: 0, y: 0, z: 0 },
            sourcePathId: path.id,
        });
    }
    return objects;
}

// --- 3D bin-packing -------------------------------------------------------

interface PlacedBox {
    min: Vector3;
    max: Vector3;
}

function boxesOverlap(a: PlacedBox, b: PlacedBox, margin: number): boolean {
    return (
        a.min.x - margin < b.max.x &&
        a.max.x + margin > b.min.x &&
        a.min.y - margin < b.max.y &&
        a.max.y + margin > b.min.y &&
        a.min.z - margin < b.max.z &&
        a.max.z + margin > b.min.z
    );
}

/**
 * Greedy collision packer: places each instance (paths flagged bin-pack, plus
 * every group child) on a jittered candidate grid inside `bounds`, rejecting
 * positions that overlap an already-placed box. Deterministic via each path's
 * seed. Free-mode objects keep their authored placement and are treated as
 * fixed obstacles so packed objects avoid them.
 */
export function packObjects(
    objects: SceneObject[],
    paths: Path3D[],
    bounds: PackingBounds,
): SceneObject[] {
    const packingByPath = new Map<string, PackingConfig>();
    for (const p of paths) packingByPath.set(p.id, p.packing);

    const placed: PlacedBox[] = [];
    const halfX = Math.max(0.1, bounds.x / 2);
    const halfY = Math.max(0.1, bounds.y / 2);
    const halfZ = Math.max(0.1, bounds.z / 2);

    // Register a fixed obstacle (free-mode object) so packing avoids it.
    const registerObstacle = (obj: SceneObject) => {
        const lb = objectLocalBounds(obj);
        placed.push({
            min: toVector3(lb.min),
            max: toVector3(lb.max),
        });
    };

    // Find a non-overlapping offset for an object of the given local bounds.
    const findOffset = (
        local: Box3Lite,
        margin: number,
        seed: number,
        instanceIndex: number,
    ): Vector3 => {
        const size = new Vector3(
            local.max.x - local.min.x,
            local.max.y - local.min.y,
            local.max.z - local.min.z,
        );
        const center = new Vector3(
            (local.min.x + local.max.x) / 2,
            (local.min.y + local.max.y) / 2,
            (local.min.z + local.max.z) / 2,
        );
        const maxAttempts = 240;
        for (let attempt = 0; attempt < maxAttempts; attempt++) {
            const salt = instanceIndex * 1000 + attempt;
            const rx = hashPlacementToUnit(seed, salt, 0x1111) * 2 - 1;
            const ry = hashPlacementToUnit(seed, salt, 0x2222) * 2 - 1;
            const rz = hashPlacementToUnit(seed, salt, 0x3333) * 2 - 1;
            // Keep the whole box inside bounds.
            const px = rx * Math.max(0, halfX - size.x / 2);
            const py = ry * Math.max(0, halfY - size.y / 2);
            const pz = rz * Math.max(0, halfZ - size.z / 2);
            const offset = new Vector3(
                px - center.x,
                py - center.y,
                pz - center.z,
            );
            const candidate: PlacedBox = {
                min: toVector3(local.min).add(offset),
                max: toVector3(local.max).add(offset),
            };
            const collides = placed.some((p) =>
                boxesOverlap(candidate, p, margin),
            );
            if (!collides) {
                placed.push(candidate);
                return offset;
            }
        }
        // Give up gracefully: place at a deterministic spot anyway.
        const offset = new Vector3(
            hashPlacementToUnit(seed, instanceIndex, 0xaaaa) * halfX -
                center.x,
            hashPlacementToUnit(seed, instanceIndex, 0xbbbb) * halfY -
                center.y,
            hashPlacementToUnit(seed, instanceIndex, 0xcccc) * halfZ -
                center.z,
        );
        placed.push({
            min: toVector3(local.min).add(offset),
            max: toVector3(local.max).add(offset),
        });
        return offset;
    };

    const anyBinPack = objects.some((o) => {
        const cfg = packingByPath.get(o.sourcePathId);
        return cfg?.mode === "bin-pack";
    });
    if (!anyBinPack) return objects;

    // First pass: free objects become obstacles.
    for (const obj of objects) {
        const cfg = packingByPath.get(obj.sourcePathId);
        if (cfg?.mode !== "bin-pack") registerObstacle(obj);
    }

    // Second pass: assign offsets to bin-pack objects.
    let globalInstance = 0;
    const out: SceneObject[] = [];
    for (const obj of objects) {
        const cfg = packingByPath.get(obj.sourcePathId);
        if (cfg?.mode !== "bin-pack") {
            out.push(obj);
            continue;
        }
        if (obj.kind === "group") {
            const children = obj.children.map((child) => {
                const local = objectLocalBounds(child);
                const offset = findOffset(
                    local,
                    cfg.margin,
                    cfg.seed,
                    globalInstance++,
                );
                return offsetObject(child, offset);
            });
            out.push({ ...obj, children, offset: { x: 0, y: 0, z: 0 } });
        } else {
            const local = objectLocalBounds(obj);
            const offset = findOffset(
                local,
                cfg.margin,
                cfg.seed,
                globalInstance++,
            );
            out.push(offsetObject(obj, offset));
        }
    }
    return out;
}

/** Return a copy of `obj` translated by `offset` (recursing groups). */
function offsetObject(obj: SceneObject, offset: Vector3): SceneObject {
    const add = (p: Vec3): Vec3 => ({
        x: p.x + offset.x,
        y: p.y + offset.y,
        z: p.z + offset.z,
    });
    if (obj.kind === "group") {
        return {
            ...obj,
            children: obj.children.map((c) => offsetObject(c, offset)),
        };
    }
    if (obj.kind === "billboard") {
        return { ...obj, center: add(obj.center) };
    }
    return { ...obj, spine: obj.spine.map(add) };
}

// --- Geometry building (shared by preview + projection) -------------------

/**
 * Build a swept primitive geometry following the full spine: one oriented
 * cuboid/cylinder per segment, or one sphere per spine vertex for "sphere".
 * Vertex positions are baked into world space (no mesh transform needed).
 */
export function buildSweptGeometry(
    spine: Vec3[],
    shape: Exclude<VolumeShape, "billboard">,
    crossSize: number,
    radialSegments: number,
): BufferGeometry | null {
    const parts: BufferGeometry[] = [];

    if (shape === "sphere") {
        for (const p of spine) {
            const g = new SphereGeometry(
                crossSize,
                radialSegments,
                Math.max(2, Math.round(radialSegments / 2)),
            );
            g.translate(p.x, p.y, p.z);
            parts.push(g);
        }
    } else {
        for (let i = 0; i < spine.length - 1; i++) {
            const a = toVector3(spine[i]!);
            const b = toVector3(spine[i + 1]!);
            const dir = b.clone().sub(a);
            const length = dir.length();
            if (length < 1e-6) continue;
            dir.normalize();

            let g: BufferGeometry;
            if (shape === "cylinder") {
                g = new CylinderGeometry(
                    crossSize,
                    crossSize,
                    length,
                    radialSegments,
                    1,
                );
            } else {
                g = new BoxGeometry(crossSize * 2, length, crossSize * 2);
            }
            // Orient local +Y onto the segment direction, then move to midpoint.
            const quat = new Quaternion().setFromUnitVectors(LOCAL_UP, dir);
            g.applyQuaternion(quat);
            const mid = a.clone().add(b).multiplyScalar(0.5);
            g.translate(mid.x, mid.y, mid.z);
            parts.push(g);
        }
    }

    if (parts.length === 0) return null;
    if (parts.length === 1) return parts[0]!;
    const merged = mergeGeometries(parts, false);
    for (const p of parts) p.dispose();
    return merged ?? null;
}

// --- Preview group (WebGL) ------------------------------------------------

/** Build a three.js Group of meshes for the live WebGL preview. */
export function buildPreviewGroup(objects: SceneObject[]): Group {
    const root = new Group();
    const addObject = (obj: SceneObject, parent: Group) => {
        if (obj.kind === "group") {
            const g = new Group();
            g.position.set(obj.offset.x, obj.offset.y, obj.offset.z);
            for (const child of obj.children) addObject(child, g);
            parent.add(g);
            return;
        }
        if (obj.kind === "billboard") {
            const geo = new PlaneGeometry(
                obj.halfExtent * 2,
                obj.halfExtent * 2,
            );
            const mat = new MeshStandardMaterial({
                color: obj.color,
                side: DoubleSide,
                roughness: 0.85,
                metalness: 0,
            });
            const mesh = new Mesh(geo, mat);
            mesh.position.set(obj.center.x, obj.center.y, obj.center.z);
            mesh.userData.billboard = true;
            parent.add(mesh);
            return;
        }
        const geo = buildSweptGeometry(
            obj.spine,
            obj.shape,
            obj.crossSize,
            obj.radialSegments,
        );
        if (!geo) return;
        const mat = new MeshStandardMaterial({
            color: obj.color,
            roughness: 0.7,
            metalness: 0.05,
        });
        parent.add(new Mesh(geo, mat));
    };
    for (const obj of objects) addObject(obj, root);
    return root;
}

export interface HandleUserData {
    handlePathId: string;
    handlePointIndex: number;
}

/**
 * Build draggable point handles for directly-authored paths. Only paths whose
 * points map unambiguously to world positions get handles — i.e. single-copy,
 * free-placement paths (packed/multi-copy spines have no single source point).
 */
export function buildHandleGroup(paths: Path3D[], handleRadius: number): Group {
    const group = new Group();
    for (const path of paths) {
        if (!path.visible) continue;
        if (path.packing.mode === "bin-pack" || path.packing.count > 1) continue;
        path.points.forEach((p, index) => {
            const geo = new SphereGeometry(handleRadius, 12, 8);
            const mat = new MeshStandardMaterial({
                color: 0xffd166,
                emissive: 0x4a3a00,
                roughness: 0.4,
            });
            const mesh = new Mesh(geo, mat);
            mesh.position.set(p.x, p.y, p.z);
            const userData: HandleUserData = {
                handlePathId: path.id,
                handlePointIndex: index,
            };
            mesh.userData = userData;
            group.add(mesh);
        });
    }
    return group;
}

/** Total preview/projection primitives (for progress totals). */
function countLeaves(objects: SceneObject[]): number {
    let n = 0;
    const walk = (o: SceneObject) => {
        if (o.kind === "group") o.children.forEach(walk);
        else n += 1;
    };
    objects.forEach(walk);
    return n;
}

// --- Projection to SVG drawables ------------------------------------------

interface RawFace {
    ndc: Array<{ x: number; y: number }>;
    depth: number;
    color: string;
}

/**
 * Project the compiled scene through the live camera into depth-sorted,
 * shaded polygon drawables. Mirrors the Grid Cubes 3D projection pipeline
 * (project -> NDC-fit -> backface cull -> Lambert shade -> painter sort).
 */
export async function projectVolumesToDrawables(
    objects: SceneObject[],
    camera: Camera,
    params: VolumeProjectionParams,
): Promise<VolumeComputeResult> {
    const {
        palette,
        ambient,
        backfaceCulling,
        outputMinX,
        outputMinY,
        outputWidth,
        outputHeight,
        signal,
        onProgress,
    } = params;
    const ambientFloor = clamp01(ambient);

    camera.updateMatrixWorld(true);
    const cameraPos = new Vector3();
    camera.getWorldPosition(cameraPos);
    const camRight = new Vector3();
    const camUp = new Vector3();
    const camForward = new Vector3();
    camera.matrixWorld.extractBasis(camRight, camUp, camForward);
    camRight.normalize();
    camUp.normalize();

    const rawFaces: RawFace[] = [];
    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;
    const track = (x: number, y: number) => {
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
    };

    const projectPoint = (
        world: Vector3,
    ): { x: number; y: number; depth: number } | null => {
        const clip = world.clone().project(camera);
        if (!Number.isFinite(clip.x) || !Number.isFinite(clip.y)) return null;
        if (clip.z < -1 || clip.z > 1) return null;
        return { x: clip.x, y: clip.y, depth: clip.z };
    };

    const pushPolygon = (corners: Vector3[], normal: Vector3, color: string) => {
        // Backface cull using the face center -> camera direction.
        const faceCenter = corners
            .reduce((acc, c) => acc.add(c), new Vector3())
            .multiplyScalar(1 / corners.length);
        const toCamera = cameraPos.clone().sub(faceCenter).normalize();
        const facing = normal.dot(toCamera);
        if (backfaceCulling && facing <= 0) return;

        const projected: Array<{ x: number; y: number }> = [];
        let depthSum = 0;
        for (const c of corners) {
            const p = projectPoint(c);
            if (!p) return;
            projected.push({ x: p.x, y: p.y });
            depthSum += p.depth;
        }
        // Shade by absolute facing so culled-disabled backfaces still read.
        const ndotl = clamp01(Math.abs(normal.dot(LIGHT_DIR)));
        const tone = ambientFloor + (1 - ambientFloor) * ndotl;
        for (const p of projected) track(p.x, p.y);
        rawFaces.push({
            ndc: projected,
            depth: depthSum / projected.length,
            color: blendColor(color, samplePalette(palette, tone), 0.65),
        });
    };

    const total = Math.max(1, countLeaves(objects));
    onProgress?.({ phase: "project", processed: 0, total });
    const budget = startFrameBudget();
    let processed = 0;

    const projectObject = async (obj: SceneObject, parentOffset: Vector3) => {
        if (obj.kind === "group") {
            const off = parentOffset.clone().add(toVector3(obj.offset));
            for (const child of obj.children) await projectObject(child, off);
            return;
        }
        await yieldIfFrameExhausted(budget, signal);
        processed += 1;

        if (obj.kind === "billboard") {
            const center = toVector3(obj.center).add(parentOffset);
            const r = camRight.clone().multiplyScalar(obj.halfExtent);
            const u = camUp.clone().multiplyScalar(obj.halfExtent);
            const corners = [
                center.clone().sub(r).sub(u),
                center.clone().add(r).sub(u),
                center.clone().add(r).add(u),
                center.clone().sub(r).add(u),
            ];
            // Billboard faces the camera; use the view forward as its normal.
            pushPolygon(corners, camForward.clone().negate(), obj.color);
            onProgress?.({ phase: "project", processed, total });
            return;
        }

        const geo = buildSweptGeometry(
            obj.spine.map((p) => ({
                x: p.x + parentOffset.x,
                y: p.y + parentOffset.y,
                z: p.z + parentOffset.z,
            })),
            obj.shape,
            obj.crossSize,
            obj.radialSegments,
        );
        if (!geo) {
            onProgress?.({ phase: "project", processed, total });
            return;
        }
        const nonIndexed = geo.index ? geo.toNonIndexed() : geo;
        const pos = nonIndexed.getAttribute("position");
        const a = new Vector3();
        const b = new Vector3();
        const c = new Vector3();
        const ab = new Vector3();
        const ac = new Vector3();
        const normal = new Vector3();
        for (let i = 0; i < pos.count; i += 3) {
            a.fromBufferAttribute(pos, i);
            b.fromBufferAttribute(pos, i + 1);
            c.fromBufferAttribute(pos, i + 2);
            ab.subVectors(b, a);
            ac.subVectors(c, a);
            normal.crossVectors(ab, ac);
            if (normal.lengthSq() < 1e-12) continue;
            normal.normalize();
            pushPolygon([a.clone(), b.clone(), c.clone()], normal, obj.color);
            if ((i / 3) % 256 === 0) {
                await yieldIfFrameExhausted(budget, signal);
            }
        }
        nonIndexed.dispose();
        if (nonIndexed !== geo) geo.dispose();
        onProgress?.({ phase: "project", processed, total });
    };

    for (const obj of objects) await projectObject(obj, new Vector3());

    if (rawFaces.length === 0) {
        return { drawables: [], objectCount: total, faceCount: 0 };
    }

    const pad = 0.05;
    const fitMinX = minX - pad;
    const fitMaxX = maxX + pad;
    const fitMinY = minY - pad;
    const fitMaxY = maxY + pad;
    const spanX = Math.max(1e-6, fitMaxX - fitMinX);
    const spanY = Math.max(1e-6, fitMaxY - fitMinY);
    const mapX = (ndcX: number) =>
        outputMinX + ((ndcX - fitMinX) / spanX) * outputWidth;
    const mapY = (ndcY: number) =>
        outputMinY + (1 - (ndcY - fitMinY) / spanY) * outputHeight;

    const drawables: VolumeDrawable[] = rawFaces.map((f) => ({
        kind: "face",
        points: f.ndc.map((p) => ({ x: mapX(p.x), y: mapY(p.y) })),
        depth: f.depth,
        color: f.color,
    }));
    // Painter's algorithm: far (larger NDC z) first.
    drawables.sort((p, q) => q.depth - p.depth);

    return {
        drawables,
        objectCount: total,
        faceCount: drawables.length,
    };
}

// --- Color helpers --------------------------------------------------------

function hexToRgb(hex: string): { r: number; g: number; b: number } {
    let h = hex.trim().replace(/^#/, "");
    if (h.length === 3) {
        h = h
            .split("")
            .map((ch) => ch + ch)
            .join("");
    }
    const int = Number.parseInt(h, 16);
    if (!Number.isFinite(int) || h.length !== 6) {
        return { r: 136, g: 136, b: 136 };
    }
    return { r: (int >> 16) & 255, g: (int >> 8) & 255, b: int & 255 };
}

function rgbToHex(r: number, g: number, b: number): string {
    const to = (n: number) =>
        Math.max(0, Math.min(255, Math.round(n)))
            .toString(16)
            .padStart(2, "0");
    return `#${to(r)}${to(g)}${to(b)}`;
}

/** Blend toward `b` by `t` (0 = all a). Used to tint the per-path color by shading. */
function blendColor(a: string, b: string, t: number): string {
    const ca = hexToRgb(a);
    const cb = hexToRgb(b);
    return rgbToHex(
        ca.r + (cb.r - ca.r) * t,
        ca.g + (cb.g - ca.g) * t,
        ca.b + (cb.b - ca.b) * t,
    );
}

// --- SVG export -----------------------------------------------------------

function round2(n: number): number {
    return Math.round(n * 100) / 100;
}

export function buildVolumeExportSvg(
    drawables: VolumeDrawable[],
    viewBoxAttr: string,
    faceStrokeEnabled: boolean,
    faceStrokeColor: string,
    faceStrokeWidth: number,
): string {
    if (drawables.length === 0) return "";
    const body = drawables
        .map((d) => {
            const pts = d.points
                .map((p) => `${round2(p.x)},${round2(p.y)}`)
                .join(" ");
            const stroke = faceStrokeEnabled
                ? ` stroke="${faceStrokeColor}" stroke-width="${faceStrokeWidth}" stroke-linejoin="round"`
                : "";
            return `<polygon points="${pts}" fill="${d.color}"${stroke} />`;
        })
        .join("\n");
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${viewBoxAttr}">
  <g class="volume-builder-3d">
${body}
  </g>
</svg>`;
}
